/**
 * The post-dictation CSAT popover: two declared names plus one token.
 *
 * The impression lands as `survey_viewed`, which no domain declares — so what
 * dictation owns of it is the `view_action` token below, bound to the app's
 * `SurveyViewedAction` by a map in the emit host. Old path keeps shims.
 */

/**
 * Which build the rating came from. On the wire for both declared names and not
 * derivable from Amplitude's auto-properties: the desktop popover is confined to
 * the logs page and the web one is global, so the split is two populations.
 */
export type DictationCsatPlatform = 'desktop' | 'web';

/**
 * The impression's `view_action`. An object so the app-side map can be keyed by it
 * and proved total — the whole population of this survey is this one token.
 */
export const DICTATION_CSAT_SURVEY_ACTION = {
  shown: 'universal_dictation_csat_shown',
} as const;

export type DictationCsatSurveyActionToken =
  (typeof DICTATION_CSAT_SURVEY_ACTION)[keyof typeof DICTATION_CSAT_SURVEY_ACTION];

/** `survey_viewed` — the popover became visible. */
export type DictationCsatViewedProps = {
  view_action: DictationCsatSurveyActionToken;
};

/** `universal_dictation_csat_dismissed` — closed without submitting a rating. */
export type DictationCsatDismissedEventProps = {
  platform: DictationCsatPlatform;
};

/** `universal_dictation_csat_submitted` — a star rating was submitted. */
export type DictationCsatSubmittedEventProps = {
  platform: DictationCsatPlatform;
  /** `number`, not the generated `CSATRating`: the wire property always was one, and this door is inert. */
  stars: number;
  /** ABSENT, not empty, when nothing was typed — a blank comment is a different answer. */
  comment?: string;
  /** `null` (not absent) when the variant query has not resolved. */
  post_processing_model?: string | null;
};

export type TrackDictationCsatViewed = (props: DictationCsatViewedProps) => void;
export type TrackDictationCsatSubmitted = (
  props: DictationCsatSubmittedEventProps
) => void;
export type TrackDictationCsatDismissed = (
  props: DictationCsatDismissedEventProps
) => void;

/**
 * All three seams as one bundle: the popover fires all three, and its
 * auto-dismiss timer fires the third with no user input.
 */
export type DictationCsatAnalyticsPorts = {
  trackViewed: TrackDictationCsatViewed;
  trackSubmitted: TrackDictationCsatSubmitted;
  trackDismissed: TrackDictationCsatDismissed;
};
